/**
 * RetainerCheckoutPage
 * Collects client contact info and desired payment types for the selected
 * service tier, then creates engagement(s) via the REDEX portal so that
 * payment links are emailed directly to the client.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  alpha,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Send,
  AttachMoney,
  Repeat,
  Star,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SERVICE_TIERS, getTier, formatPrice } from '../lib/serviceTiers';
import {
  createRetainerEngagement,
  RequestType,
  CreatedEngagement,
} from '../services/engagementService';

// ─── Custom-dev virtual tier (not in SERVICE_TIERS) ───────────────────────────

const CUSTOM_DEV_TIER = {
  id: 'custom-dev',
  name: 'Custom Development',
  tagline: 'Bespoke engineering engagement',
  description: 'Dedicated quarterly retainer + custom monthly development allocation',
  features: [
    'Dedicated engineering squad',
    'Quarterly strategic retainer ($24,997)',
    'Custom monthly development ($7,000/mo)',
    'Unlimited scope revisions',
    '24/7 engineering SLA',
    'SOC2/HIPAA compliance available',
  ],
};

// ─── Payment option definitions per tier ─────────────────────────────────────

interface PaymentOption {
  key: RequestType;
  label: string;
  sublabel: string;
  amount: string;
  icon: React.ReactNode;
}

function getPaymentOptions(tierId: string): PaymentOption[] {
  if (tierId === 'enterprise') {
    return [
      {
        key: 'quarterly',
        label: 'Quarterly Retainer',
        sublabel: 'Recurring every 3 months',
        amount: '$24,999.97 / quarter',
        icon: <Repeat fontSize="small" />,
      },
    ];
  }

  if (tierId === 'custom-dev') {
    return [
      {
        key: 'quarterly',
        label: 'Quarterly Engineering Retainer',
        sublabel: 'Recurring every 3 months',
        amount: '$24,997 / quarter',
        icon: <Repeat fontSize="small" />,
      },
      {
        key: 'custom-monthly',
        label: 'Custom Monthly Development',
        sublabel: 'Recurring monthly',
        amount: '$7,000 / month',
        icon: <Repeat fontSize="small" />,
      },
    ];
  }

  const tier = getTier(tierId);
  if (!tier) return [];

  const opts: PaymentOption[] = [
    {
      key: 'setup',
      label: 'Setup Retainer',
      sublabel: 'One-time onboarding fee',
      amount: formatPrice(tier.retainerFee),
      icon: <AttachMoney fontSize="small" />,
    },
  ];

  if (tier.monthlyFee > 0) {
    opts.push({
      key: 'monthly',
      label: 'Monthly Maintenance',
      sublabel: 'Recurring monthly retainer',
      amount: `${formatPrice(tier.monthlyFee)} / mo`,
      icon: <Repeat fontSize="small" />,
    });
  }

  return opts;
}

// ─── Tier display helper ──────────────────────────────────────────────────────

function useTierInfo(tierId: string) {
  if (tierId === 'custom-dev') return CUSTOM_DEV_TIER;
  if (tierId === 'enterprise') {
    const t = getTier('enterprise');
    return t ?? null;
  }
  return getTier(tierId) ?? null;
}

// ─── Component ────────────────────────────────────────────────────────────────

type PageState = 'form' | 'submitting' | 'success' | 'error';

const RetainerCheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tierId = searchParams.get('tier') ?? 'professional';

  const tier = useTierInfo(tierId);
  const paymentOptions = getPaymentOptions(tierId);

  // ── Form state ──────────────────────────────────────────────────────────────
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactCompany, setContactCompany] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Set<RequestType>>(
    // Pre-select all options by default
    () => new Set(paymentOptions.map((o) => o.key)),
  );
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  // ── Page state ──────────────────────────────────────────────────────────────
  const [pageState, setPageState] = useState<PageState>('form');
  const [successEngagements, setSuccessEngagements] = useState<CreatedEngagement[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  // Redirect if tier is unknown
  useEffect(() => {
    const validTiers = [...SERVICE_TIERS.map((t) => t.id), 'custom-dev'];
    if (!validTiers.includes(tierId)) {
      navigate('/pricing', { replace: true });
    }
  }, [tierId, navigate]);

  // Re-sync default selections when tier changes
  useEffect(() => {
    setSelectedTypes(new Set(paymentOptions.map((o) => o.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierId]);

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateName = (v: string) => {
    if (!v.trim()) { setNameError('Name is required'); return false; }
    setNameError('');
    return true;
  };

  const validateEmail = (v: string) => {
    if (!v) { setEmailError('Email is required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError('Enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const toggleType = (key: RequestType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const isValid =
    contactName.trim() &&
    !nameError &&
    contactEmail &&
    !emailError &&
    selectedTypes.size > 0;

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const nameOk = validateName(contactName);
    const emailOk = validateEmail(contactEmail);
    if (!nameOk || !emailOk || selectedTypes.size === 0) return;

    setPageState('submitting');
    try {
      const result = await createRetainerEngagement({
        tierId,
        contactName: contactName.trim(),
        contactEmail,
        contactCompany: contactCompany.trim() || undefined,
        requestTypes: Array.from(selectedTypes),
      });

      if (result.success) {
        setSuccessEngagements(result.engagements ?? []);
        setPageState('success');
      } else {
        setErrorMessage(result.message || 'Something went wrong. Please try again.');
        setPageState('error');
      }
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message ?? err.message ?? 'Request failed. Please try again.',
      );
      setPageState('error');
    }
  };

  // ─── Success screen ──────────────────────────────────────────────────────────
  if (pageState === 'success') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0A0E27', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: alpha('#fff', 0.04),
              border: '1px solid',
              borderColor: alpha('#fff', 0.1),
              borderRadius: 3,
            }}
          >
            <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={800} color="white" gutterBottom>
              Payment Request{successEngagements.length > 1 ? 's' : ''} Sent
            </Typography>
            <Typography color="grey.400" sx={{ mb: 4 }}>
              We've sent payment link{successEngagements.length > 1 ? 's' : ''} to{' '}
              <strong style={{ color: 'white' }}>{contactEmail}</strong>. Complete payment at
              your convenience — links include ACH, USDC, and card options.
            </Typography>

            {successEngagements.length > 0 && (
              <Stack spacing={1.5} sx={{ mb: 4, textAlign: 'left' }}>
                {successEngagements.map((eng) => (
                  <Box
                    key={eng.id}
                    sx={{
                      p: 2,
                      bgcolor: alpha('#fff', 0.04),
                      border: '1px solid',
                      borderColor: alpha('#fff', 0.08),
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="white">
                        {eng.title}
                      </Typography>
                      <Typography variant="caption" color="grey.500">
                        {eng.type === 'one_time' ? 'One-time' : 'Recurring'} ·{' '}
                        {formatPrice(eng.amount_cents)}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                  </Box>
                ))}
              </Stack>
            )}

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => navigate('/pricing')}
                sx={{ borderColor: alpha('#fff', 0.3), color: 'white' }}
              >
                Back to Pricing
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                Sign In to Portal
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!tier) return null;

  // ─── Form screen ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0A0E27' }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: 'transparent', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${alpha('#fff', 0.08)}` }}
      >
        <Toolbar>
          <Typography
            variant="h5"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            REDEX LABS
          </Typography>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/pricing')}
            sx={{ color: alpha('#fff', 0.7) }}
          >
            Back to Pricing
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Page heading */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            label="Retainer Request"
            size="small"
            sx={{ mb: 2, bgcolor: alpha('#667eea', 0.15), color: '#a5b4fc', border: '1px solid', borderColor: alpha('#667eea', 0.3) }}
          />
          <Typography
            variant="h3"
            fontWeight={900}
            sx={{
              background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Request Your Service
          </Typography>
          <Typography color="grey.400">
            We'll send secure payment links directly to your inbox.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* ── Left: Plan Summary ──────────────────────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                bgcolor: alpha('#fff', 0.03),
                border: '1px solid',
                borderColor: alpha('#667eea', 0.3),
                borderRadius: 3,
                position: 'sticky',
                top: 88,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {'recommended' in tier && tier.recommended && (
                  <Chip
                    label="MOST POPULAR"
                    color="primary"
                    icon={<Star sx={{ fontSize: 14 }} />}
                    size="small"
                    sx={{ mb: 2, fontWeight: 700 }}
                  />
                )}
                <Typography variant="h6" fontWeight={700} color="white" gutterBottom>
                  {tier.name}
                </Typography>
                <Typography variant="body2" color="grey.500" sx={{ mb: 3 }}>
                  {tier.tagline}
                </Typography>

                <Divider sx={{ borderColor: alpha('#fff', 0.08), mb: 2 }} />

                {/* Pricing summary */}
                {tierId === 'enterprise' ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" fontWeight={800} color="primary.light">
                      $24,999.97
                    </Typography>
                    <Typography variant="caption" color="grey.500">per quarter · recurring</Typography>
                  </Box>
                ) : tierId === 'custom-dev' ? (
                  <Stack spacing={1} sx={{ mb: 3 }}>
                    <Box>
                      <Typography variant="h5" fontWeight={800} color="primary.light">$24,997</Typography>
                      <Typography variant="caption" color="grey.500">per quarter · recurring</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="primary.light">+ $7,000</Typography>
                      <Typography variant="caption" color="grey.500">per month · custom dev</Typography>
                    </Box>
                  </Stack>
                ) : (
                  (() => {
                    const t = getTier(tierId)!;
                    return (
                      <Stack spacing={1} sx={{ mb: 3 }}>
                        <Box>
                          <Typography variant="h5" fontWeight={800} color="primary.light">
                            {formatPrice(t.retainerFee)}
                          </Typography>
                          <Typography variant="caption" color="grey.500">one-time setup</Typography>
                        </Box>
                        {t.monthlyFee > 0 && (
                          <Box>
                            <Typography variant="h6" fontWeight={700} color="primary.light">
                              + {formatPrice(t.monthlyFee)} / mo
                            </Typography>
                            <Typography variant="caption" color="grey.500">monthly retainer</Typography>
                          </Box>
                        )}
                      </Stack>
                    );
                  })()
                )}

                <Divider sx={{ borderColor: alpha('#fff', 0.08), mb: 2 }} />

                <List dense disablePadding>
                  {tier.features.slice(0, 6).map((f, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.4 }}>
                      <ListItemIcon sx={{ minWidth: 26 }}>
                        <CheckCircle sx={{ fontSize: 15, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={f}
                        primaryTypographyProps={{ variant: 'caption', color: 'grey.400' }}
                      />
                    </ListItem>
                  ))}
                  {tier.features.length > 6 && (
                    <Typography variant="caption" color="grey.600" sx={{ pl: 3.5 }}>
                      + {tier.features.length - 6} more
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* ── Right: Form ─────────────────────────────────────────────────── */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* Payment type selector */}
              <Paper
                sx={{
                  p: 3,
                  bgcolor: alpha('#fff', 0.03),
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.08),
                  borderRadius: 3,
                }}
              >
                <Typography variant="h6" fontWeight={700} color="white" gutterBottom>
                  Payment Requests to Send
                </Typography>
                <Typography variant="body2" color="grey.500" sx={{ mb: 2 }}>
                  We'll email a secure payment link for each selected item.
                </Typography>

                <FormGroup>
                  {paymentOptions.map((opt) => (
                    <Box
                      key={opt.key}
                      onClick={() => toggleType(opt.key)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        mb: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: selectedTypes.has(opt.key)
                          ? alpha('#667eea', 0.5)
                          : alpha('#fff', 0.08),
                        bgcolor: selectedTypes.has(opt.key)
                          ? alpha('#667eea', 0.08)
                          : 'transparent',
                        transition: 'all 0.2s',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedTypes.has(opt.key)}
                            onChange={() => toggleType(opt.key)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ color: 'grey.600', '&.Mui-checked': { color: 'primary.light' } }}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                      <Box sx={{ color: 'primary.light' }}>{opt.icon}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={600} color="white">
                          {opt.label}
                        </Typography>
                        <Typography variant="caption" color="grey.500">
                          {opt.sublabel}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={700} color="primary.light">
                        {opt.amount}
                      </Typography>
                    </Box>
                  ))}
                </FormGroup>

                {selectedTypes.size === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Select at least one payment request to continue.
                  </Alert>
                )}
              </Paper>

              {/* Contact info */}
              <Paper
                sx={{
                  p: 3,
                  bgcolor: alpha('#fff', 0.03),
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.08),
                  borderRadius: 3,
                }}
              >
                <Typography variant="h6" fontWeight={700} color="white" gutterBottom>
                  Contact Information
                </Typography>
                <Typography variant="body2" color="grey.500" sx={{ mb: 3 }}>
                  Payment links will be sent to this email address.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={contactName}
                      onChange={(e) => { setContactName(e.target.value); if (e.target.value) validateName(e.target.value); }}
                      onBlur={() => validateName(contactName)}
                      error={!!nameError}
                      helperText={nameError}
                      required
                      InputLabelProps={{ sx: { color: 'grey.500' } }}
                      InputProps={{ sx: { color: 'white' } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: alpha('#fff', 0.15) },
                          '&:hover fieldset': { borderColor: alpha('#fff', 0.3) },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      value={contactCompany}
                      onChange={(e) => setContactCompany(e.target.value)}
                      InputLabelProps={{ sx: { color: 'grey.500' } }}
                      InputProps={{ sx: { color: 'white' } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: alpha('#fff', 0.15) },
                          '&:hover fieldset': { borderColor: alpha('#fff', 0.3) },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => { setContactEmail(e.target.value); if (e.target.value) validateEmail(e.target.value); }}
                      onBlur={() => validateEmail(contactEmail)}
                      error={!!emailError}
                      helperText={emailError}
                      required
                      InputLabelProps={{ sx: { color: 'grey.500' } }}
                      InputProps={{ sx: { color: 'white' } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: alpha('#fff', 0.15) },
                          '&:hover fieldset': { borderColor: alpha('#fff', 0.3) },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Error alert */}
              {pageState === 'error' && (
                <Alert severity="error" onClose={() => setPageState('form')}>
                  {errorMessage}
                </Alert>
              )}

              {/* Submit */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={!isValid || pageState === 'submitting'}
                onClick={handleSubmit}
                startIcon={pageState === 'submitting' ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 700,
                  background: isValid
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : undefined,
                  borderRadius: 2,
                }}
              >
                {pageState === 'submitting'
                  ? 'Sending Payment Request…'
                  : `Send Payment Request${selectedTypes.size > 1 ? 's' : ''}`}
              </Button>

              <Typography variant="caption" color="grey.600" textAlign="center">
                Payment links are processed securely via the REDEX portal. ACH, USDC, and card accepted.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default RetainerCheckoutPage;
